import { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import axios from 'axios'
import { TOKEN_TYPE, getTokenFromCookies, clearAllTokens, setTokenToCookies, setTokenExpiry } from '@/lib/cookies'
import { getRootDomain } from '@/lib/domain'
import { authAPI } from '../api/api'
import type { TokenPair } from '../api/types'
import {
  setLoading,
  setError,
  loginSuccess,
  logout as logoutAction,
} from '../store/authSlice'
import {
  selectAuth,
  selectUser,
  selectIsAuthenticated,
} from '../store/selectors'

function getCookieDomain(): string | undefined {
  const root = getRootDomain(window.location.hostname)
  return root ? `.${root}` : undefined
}

function saveTokens(tokens: TokenPair, domain?: string) {
  const expiresAt = new Date(Date.now() + tokens.expires_in * 1000)
  setTokenToCookies(TOKEN_TYPE.ACCESS_TOKEN, tokens.access_token, tokens.expires_in, domain)
  setTokenToCookies(TOKEN_TYPE.REFRESH_TOKEN, tokens.refresh_token, undefined, domain)
  setTokenExpiry(expiresAt.toISOString(), domain)
}

export const useAuth = () => {
  const dispatch = useDispatch()
  const authState = useSelector(selectAuth)
  const user = useSelector(selectUser)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const checkingRef = useRef(false)

  const refreshToken = async (): Promise<boolean> => {
    const refreshTokenValue = getTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN)
    if (!refreshTokenValue) {
      clearAllTokens(getCookieDomain())
      dispatch(logoutAction())
      return false
    }

    try {
      const response = await authAPI.refreshToken(refreshTokenValue)
      if (response.success) {
        saveTokens(response.data, getCookieDomain())
        scheduleTokenRefresh()
        return true
      } else {
        throw new Error('Refresh failed')
      }
    } catch (error) {
      // Chỉ clear tokens và logout khi nhận được phản hồi lỗi xác thực rõ ràng từ server
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status
        if (status === 400 || status === 401 || status === 403) {
          clearAllTokens(getCookieDomain())
          dispatch(logoutAction())
        }
      }
      return false
    }
  }

  const scheduleTokenRefresh = () => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }

    const expiresAt = getTokenFromCookies(TOKEN_TYPE.EXPIRES_AT)
    if (!expiresAt) return

    const expireTime = new Date(expiresAt)
    const now = new Date()
    const refreshTime = new Date(expireTime.getTime() - 30 * 1000)
    const timeUntilRefresh = refreshTime.getTime() - now.getTime()

    if (timeUntilRefresh > 0) {
      refreshTimeoutRef.current = setTimeout(() => {
        refreshToken()
      }, timeUntilRefresh)
    }
  }

  const checkAuth = async () => {
    if (checkingRef.current) return
    checkingRef.current = true

    const accessToken = getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN)
    const refreshTokenValue = getTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN)

    // Không có cả access lẫn refresh → unauthenticated, để AuthGuard redirect SSO
    if (!accessToken && !refreshTokenValue) {
      dispatch(setLoading(false))
      checkingRef.current = false
      return
    }

    scheduleTokenRefresh()

    // Nếu có refresh token mà không có access token -> thử refresh trước khi gọi /me
    if (!accessToken && refreshTokenValue) {
      const success = await refreshToken()
      if (!success) {
        checkingRef.current = false
        return
      }
    }

    // Đã có user trong store thì khỏi gọi /me
    if (user && isAuthenticated) {
      dispatch(setLoading(false))
      checkingRef.current = false
      return
    }

    try {
      // Interceptor sẽ tự refresh nếu access token hết hạn
      const response = await authAPI.me()
      if (response.success) {
        dispatch(loginSuccess(response.data))
      } else {
        clearAllTokens(getCookieDomain())
        dispatch(logoutAction())
      }
    } catch (error) {
      // Chỉ clear tokens và logout khi nhận được phản hồi lỗi xác thực rõ ràng từ server
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status
        if (status === 401 || status === 403) {
          clearAllTokens(getCookieDomain())
          dispatch(logoutAction())
        }
      }
    } finally {
      checkingRef.current = false
    }
  }

  useEffect(() => {
    let isMounted = true

    const runCheckAuth = async () => {
      if (isMounted) {
        await checkAuth()
      }
    }

    runCheckAuth()

    return () => {
      isMounted = false
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
        refreshTimeoutRef.current = null
      }
    }
  }, [])

  const login = async (email: string, password: string) => {
    const data = await authAPI.login(email, password)

    if (!data.success) {
      dispatch(setError(data.message))
      throw new Error(data.message)
    }

    if (data.messageCode === 'LOGIN_SUCCESS') {
      const tokens = data.data as TokenPair
      saveTokens(tokens, getCookieDomain())
      scheduleTokenRefresh()

      const meResponse = await authAPI.me()
      if (meResponse.success) {
        dispatch(loginSuccess(meResponse.data))
      }

      return { success: true, tokens }
    }

    throw new Error('Unexpected login response')
  }

  const logout = async () => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
      refreshTimeoutRef.current = null
    }

    try {
      await authAPI.logout()
    } catch {
      // Ignore logout API errors
    } finally {
      clearAllTokens(getCookieDomain())
      dispatch(logoutAction())
    }
  }

  const hasRole = (requiredRoles: string | string[]) => {
    if (!user) return false
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles]
    return roles.includes(user.role)
  }

  const hasAdminAccess = () => {
    if (!user) return false
    return user.role === 'admin' || user.role === 'super_admin'
  }

  return {
    ...authState,
    login,
    logout,
    checkAuth,
    hasRole,
    hasAdminAccess,
  }
}
